import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OrgLeaveManagementApi } from '@/services/org-leave-management.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';

/** Org tier — Leave Report, one staff member's month-by-month leave balance for a school in scope. Matches real legacy's `/leave/report` exactly. */
export function OrgLeaveReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [userId, setUserId] = useState('');

  const { data: schools = [] } = useQuery({ queryKey: ['org-leave-schools'], queryFn: () => OrgLeaveManagementApi.schools() });
  const { data: staffOptions = [] } = useQuery({
    queryKey: ['org-leave-staff-options', schoolId],
    queryFn: () => OrgLeaveManagementApi.staffOptions(schoolId),
    enabled: !!schoolId,
  });

  const { data: report, isFetching } = useQuery({
    queryKey: ['org-leave-report', schoolId, userId],
    queryFn: () => OrgLeaveManagementApi.report(schoolId, userId),
    enabled: !!schoolId && !!userId,
  });

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setUserId('');
  }

  return (
    <div>
      <PageHeader title="Manage Leave Details" description="A staff member's month-by-month leave allocation and usage, for a school in your organization." />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select className="w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">— Select a school —</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="w-56" value={userId} onChange={(e) => setUserId(e.target.value)} disabled={!schoolId}>
          <option value="">Select Staff</option>
          {staffOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name ?? '—'}
            </option>
          ))}
        </Select>
      </div>

      {!schoolId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a school to view its leave report.</div>
      ) : !userId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a staff member to view their leave details.</div>
      ) : isFetching && !report ? (
        <div className="card p-8 text-center text-sm text-slate-400">Loading…</div>
      ) : !report?.configured ? (
        <div className="card p-8 text-center text-sm text-slate-400">
          No leave settings configured for this school's current academic year — nothing to report yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th rowSpan={2} className="px-4 py-2 text-left align-bottom font-medium text-slate-600">
                  No.
                </th>
                <th rowSpan={2} className="px-4 py-2 text-left align-bottom font-medium text-slate-600">
                  Month
                </th>
                <th rowSpan={2} className="px-4 py-2 text-left align-bottom font-medium text-slate-600">
                  Allocated
                </th>
                <th colSpan={3} className="border-l border-slate-200 px-4 py-1.5 text-center font-medium text-slate-600">
                  Used
                </th>
                <th colSpan={2} className="border-l border-slate-200 px-4 py-1.5 text-center font-medium text-slate-600">
                  Remaining
                </th>
              </tr>
              <tr>
                <th className="border-l border-slate-200 px-4 py-2 text-left font-medium text-slate-600">CL (Casual Leave)</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">LWP (Leave Without Pay)</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Total</th>
                <th className="border-l border-slate-200 px-4 py-2 text-left font-medium text-slate-600">CL</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.months.map((m, i) => (
                <tr key={m.month} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-slate-800">{m.month}</td>
                  <td className="px-4 py-2 text-slate-600">{m.allocated}</td>
                  <td className="border-l border-slate-100 px-4 py-2 text-slate-600">{m.usedCl || '-'}</td>
                  <td className="px-4 py-2 text-slate-600">{m.usedLwp || '-'}</td>
                  <td className="px-4 py-2 text-slate-600">{m.usedTotal || '-'}</td>
                  <td className="border-l border-slate-100 px-4 py-2 text-slate-600">{m.remainingCl}</td>
                  <td className="px-4 py-2 text-slate-600">{m.remainingTotal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
